import { createContext, type FC, type ReactNode, useContext, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { DatesProvider } from '@mantine/dates';
import dayjs from 'dayjs';

import { bccApi, type IUser } from '@bcc-life/sandbox';

import 'dayjs/locale/ru';
import 'dayjs/locale/kk';

import { storageKeys } from '@bcc-life/sandbox/consts';

type IUserContext = {
  userMe?: IUser;
  userMeError: boolean;
  userMeFetching: boolean;
  userMeLoading: boolean;
};

export const UserContext = createContext<IUserContext>({
  userMe: undefined,
  userMeError: false,
  userMeFetching: !!localStorage.getItem(storageKeys.tokenAccess),
  userMeLoading: !!localStorage.getItem(storageKeys.tokenAccess),
});

export const UserContextProvider: FC<{ children?: ReactNode }> = (props) => {
  const userMeQuery = bccApi.user.users.useUserCurrent();
  const userMe = userMeQuery.data?.data;
  const {
    i18n: { language },
  } = useTranslation();

  const locale = useMemo(() => (language == 'kz' ? 'kk' : 'ru'), [language]);

  useMemo(() => {
    dayjs.locale(locale);
    document.documentElement.lang = locale;
  }, [locale]);

  return (
    <DatesProvider settings={{ locale }}>
      <UserContext.Provider
        value={{
          userMe: userMe,
          userMeError: userMeQuery.isError,
          userMeFetching: userMeQuery.isFetching,
          userMeLoading: userMeQuery.isLoading,
        }}
      >
        {props.children}
      </UserContext.Provider>
    </DatesProvider>
  );
};

export const useUserContext = () => {
  const userContext = useContext(UserContext);

  if (typeof userContext === 'undefined') {
    throw new Error('useUserContext must be used within a UserContextProvider');
  }

  return userContext;
};
